import React, { useState, useEffect, useCallback } from 'react';

/**
 * Bot激活组件
 * 用户输入CDK激活码来获得Bot功能权限
 */
function BotActivation({ userId }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // 加载激活状态
  const loadStatus = useCallback(async () => {
    try {
      const response = await fetch('/api/activation/status', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setStatus(data);
    } catch (err) {
      console.error('加载激活状态失败:', err);
      setError('加载状态失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // 提交激活码
  const handleActivate = async (e) => {
    e.preventDefault();
    
    if (!code.trim()) {
      setError('请输入激活码');
      return;
    }
    
    setActivating(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await fetch('/api/activation/activate', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code: code.trim() })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        setError(data.message || '激活失败');
        return;
      }
      
      setSuccess(`🎉 ${data.message}\n\n您已获得:\n• ${data.data.max_rooms} 个直播间配额\n• ${data.data.max_qq_groups} 个QQ群配额`);
      setCode('');
      loadStatus();
      
    } catch (err) {
      console.error('激活失败:', err);
      setError('激活失败，请稍后重试');
    } finally {
      setActivating(false);
    }
  };

  // 格式化激活码输入（自动添加横杠）
  const handleCodeChange = (e) => {
    let value = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
    
    // 如果用户没有输入横杠，自动格式化
    if (!value.includes('-')) {
      value = value.replace(/(.{4})(?=.)/g, '$1-');
    }
    
    // 限制长度（XXXX-XXXX-XXXX = 14字符）
    if (value.length > 14) {
      value = value.substring(0, 14);
    }
    
    setCode(value);
    setError('');
  };

  if (loading) {
    return (
      <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-light)' }}>
        加载中...
      </div>
    );
  }

  // 样式定义
  const cardStyle = {
    padding: '1.5rem',
    backgroundColor: 'var(--bg-light)',
    borderRadius: '12px',
    border: '1px solid var(--bg-purple-light)',
    marginTop: '1rem'
  };

  const titleStyle = {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: 'var(--text-dark)',
    marginBottom: '1rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  };

  const inputStyle = {
    width: '100%',
    padding: '0.75rem 1rem',
    fontSize: '1.2rem',
    fontFamily: '"SF Mono", "Fira Code", "Consolas", monospace',
    letterSpacing: '0.15em',
    textAlign: 'center',
    border: '2px solid var(--bg-purple-light)',
    borderRadius: '8px',
    outline: 'none',
    transition: 'border-color 0.2s',
    textTransform: 'uppercase'
  };

  const buttonStyle = {
    width: '100%',
    padding: '0.75rem',
    fontSize: '1rem',
    fontWeight: '600',
    color: 'white',
    backgroundColor: 'var(--primary-purple)',
    border: 'none',
    borderRadius: '8px',
    cursor: activating ? 'not-allowed' : 'pointer',
    opacity: activating ? 0.7 : 1,
    marginTop: '1rem',
    transition: 'opacity 0.2s, transform 0.1s'
  };

  const statusBadgeStyle = (isActive) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 1rem',
    borderRadius: '20px',
    fontSize: '0.875rem',
    fontWeight: '500',
    backgroundColor: isActive ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)',
    color: isActive ? '#059669' : '#d97706',
    marginBottom: '1rem'
  });

  // 已激活状态
  if (status?.is_activated) {
    return (
      <div style={cardStyle}>
        <div style={titleStyle}>
          <span>🤖</span>
          <span>Bot 激活状态</span>
        </div>
        
        <div style={statusBadgeStyle(true)}>
          <span>✅</span>
          <span>已激活</span>
        </div>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(2, 1fr)', 
          gap: '1rem',
          marginTop: '1rem'
        }}>
          <div style={{
            padding: '1rem',
            backgroundColor: 'white',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--primary-purple)' }}>
              {status.current_rooms} / {status.max_rooms}
            </div>
            <div style={{ fontSize: '0.875rem', color: 'var(--text-light)' }}>
              直播间配额
            </div>
          </div>
          
          <div style={{
            padding: '1rem',
            backgroundColor: 'white',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--primary-purple)' }}>
              {status.max_qq_groups}
            </div>
            <div style={{ fontSize: '0.875rem', color: 'var(--text-light)' }}>
              QQ群配额
            </div>
          </div>
        </div>
        
        <div style={{
          marginTop: '1rem',
          padding: '0.75rem',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          borderRadius: '8px',
          fontSize: '0.875rem',
          color: '#059669'
        }}>
          💡 您可以在「Bot配置」页面配置您的直播间功能
        </div>
      </div>
    );
  }

  // 未激活状态
  return (
    <div style={cardStyle}>
      <div style={titleStyle}>
        <span>🔑</span>
        <span>激活 Bot 功能</span>
      </div>
      
      <div style={statusBadgeStyle(false)}>
        <span>⏳</span>
        <span>未激活</span>
      </div>

      {!status?.has_bilibili_binding ? (
        <div style={{
          padding: '1rem',
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          borderRadius: '8px',
          marginBottom: '1rem'
        }}>
          <div style={{ fontWeight: '500', color: '#d97706', marginBottom: '0.5rem' }}>
            ⚠️ 请先绑定B站账号
          </div>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-light)' }}>
            激活Bot功能前需要先绑定您的B站账号
          </div>
        </div>
      ) : (
        <>
          <div style={{
            padding: '1rem',
            backgroundColor: 'rgba(99, 102, 241, 0.05)',
            borderRadius: '8px',
            marginBottom: '1rem'
          }}>
            <div style={{ fontWeight: '500', marginBottom: '0.75rem' }}>
              📋 获取激活码步骤：
            </div>
            <ol style={{ 
              margin: 0, 
              paddingLeft: '1.25rem',
              fontSize: '0.9rem',
              color: 'var(--text-light)',
              lineHeight: '1.8'
            }}>
              <li>加入 Anna Bot 官方群</li>
              <li>在群里发送 <code style={{
                backgroundColor: 'rgba(99, 102, 241, 0.1)',
                padding: '0.1rem 0.4rem',
                borderRadius: '4px',
                fontFamily: 'monospace'
              }}>/激活</code></li>
              <li>复制Bot回复的激活码</li>
              <li>在下方输入激活码完成激活</li>
            </ol>
          </div>

          <form onSubmit={handleActivate}>
            <input
              type="text"
              value={code}
              onChange={handleCodeChange}
              placeholder="XXXX-XXXX-XXXX"
              style={inputStyle}
              disabled={activating}
            />
            
            {error && (
              <div style={{
                marginTop: '0.75rem',
                padding: '0.75rem',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                borderRadius: '8px',
                color: '#dc2626',
                fontSize: '0.875rem'
              }}>
                ❌ {error}
              </div>
            )}
            
            {success && (
              <div style={{
                marginTop: '0.75rem',
                padding: '0.75rem',
                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                borderRadius: '8px',
                color: '#059669',
                fontSize: '0.875rem',
                whiteSpace: 'pre-line'
              }}>
                {success}
              </div>
            )}
            
            <button
              type="submit"
              style={buttonStyle}
              disabled={activating || !code.trim()}
            >
              {activating ? '激活中...' : '🚀 激活'}
            </button>
          </form>
        </>
      )}
      
      <div style={{
        marginTop: '1rem',
        fontSize: '0.8rem',
        color: 'var(--text-light)',
        textAlign: 'center'
      }}>
        激活后您将获得配置直播间Bot功能的权限
      </div>
    </div>
  );
}

export default BotActivation;
